
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export function SampleLinkGenerator() {
  const [schoolId, setSchoolId] = useState("12345");
  const [productName, setProductName] = useState("pordi");
  const [token, setToken] = useState("969o7yz73f9tbum2");
  const [copied, setCopied] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<"checking" | "valid" | "invalid" | "none">("checking");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Validate token on component mount and when token or schoolId changes
  useEffect(() => {
    if (!token) {
      setTokenStatus("none");
      return;
    }

    const validateToken = async () => {
      setTokenStatus("checking");
      
      try {
        // Call the edge function to check token validity - this uses the service role
        // to bypass RLS and validate the token directly
        const { data, error } = await supabase.functions.invoke("token-validator", {
          body: { schoolId, token }
        });
        
        if (error) {
          console.error("Error validating token:", error);
          setTokenStatus("invalid");
          return;
        }
        
        setTokenStatus(data.valid ? "valid" : "invalid");
      } catch (err) {
        console.error("Failed to validate token:", err);
        setTokenStatus("invalid");
      }
    };
    
    validateToken();
  }, [token, schoolId]);

  const generateLink = () => {
    return `/product?name=${encodeURIComponent(productName)}&schoolid=${encodeURIComponent(schoolId)}${token ? `&token=${encodeURIComponent(token)}` : ''}`;
  };

  const handleCopyLink = () => {
    const link = window.location.origin + generateLink();
    navigator.clipboard.writeText(link);
    setCopied(true);
    
    toast({
      title: "Link copied to clipboard",
      description: "You can now paste the link in your browser",
    });
    
    // Reset the copied state after 2 seconds
    setTimeout(() => setCopied(false), 2000);
  };

  const handleNavigateToLink = () => {
    const link = generateLink();
    window.location.href = link;
  };

  const generateNewToken = async () => {
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke("generate-token", {
        body: { schoolId }
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (data && data.token) {
        setToken(data.token);
        setTokenStatus("valid");
        
        toast({
          title: "Token generated successfully",
          description: `New token created for school ${schoolId}`,
        });
      }
    } catch (err) {
      console.error("Error generating token:", err);
      toast({
        variant: "destructive",
        title: "Failed to generate token",
        description: "An error occurred while generating a new token",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getTokenStatusUI = () => {
    switch (tokenStatus) {
      case "checking":
        return <div className="flex items-center text-blue-500"><RefreshCw className="mr-2 h-4 w-4 animate-spin" /> Checking token...</div>;
      case "valid":
        return <div className="flex items-center text-green-500"><CheckCircle2 className="mr-2 h-4 w-4" /> Token is valid</div>;
      case "invalid":
        return <div className="flex items-center text-red-500"><AlertCircle className="mr-2 h-4 w-4" /> Token is invalid</div>;
      case "none":
        return <div className="text-gray-500">No token provided</div>;
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle>Product Subscription Link Generator</CardTitle>
        <CardDescription>
          Generate test links for the public product subscription page
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-2">
          <Label htmlFor="productName">Product Name</Label>
          <Select value={productName} onValueChange={setProductName}>
            <SelectTrigger id="productName">
              <SelectValue placeholder="Select product" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pordi">Pordi</SelectItem>
              <SelectItem value="kasflow">Kasflow</SelectItem>
              <SelectItem value="pembayaran">Pembayaran</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="grid gap-2">
          <Label htmlFor="schoolId">School ID</Label>
          <Input
            id="schoolId"
            placeholder="Enter school ID"
            value={schoolId}
            onChange={(e) => setSchoolId(e.target.value)}
          />
        </div>
        
        <div className="grid gap-2">
          <div className="flex justify-between items-center">
            <Label htmlFor="token">Token</Label>
            <Button 
              type="button" 
              variant="ghost" 
              size="sm" 
              onClick={generateNewToken}
              disabled={isLoading}
              className="h-8 px-2 text-xs"
            >
              {isLoading ? <RefreshCw className="h-3 w-3 animate-spin mr-1" /> : null}
              Generate New
            </Button>
          </div>
          <Input
            id="token"
            placeholder="Enter token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
          />
          <div className="text-sm mt-1">
            {getTokenStatusUI()}
          </div>
        </div>
        
        <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
          <p className="text-sm font-mono text-gray-700 break-all">
            {window.location.origin}{generateLink()}
          </p>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between space-x-2">
        <Button variant="outline" onClick={handleCopyLink} className="flex-1">
          {copied ? <CheckCircle2 className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
          Copy Link
        </Button>
        <Button onClick={handleNavigateToLink} className="flex-1" disabled={tokenStatus !== "valid" && token !== ""}>
          Open Link
        </Button>
      </CardFooter>
    </Card>
  );
}
